<script lang="ts" setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import type { Props } from './types'
import { whatsappListingNumbers } from '@/config/whatsappListingNumbers'
import type { LocaleCode } from '@/config/locale-domains'

const props = defineProps<Props>()
const emit = defineEmits(['showCta', 'clickPersonalizePlanCta'])

const observableElementsVisibility = ref<{ [key: string]: boolean }>({})
const samPresentationSectionVisible = ref(false)
const showCta = ref(true)
const { t, locale } = useI18n()
const { $whatsappNumbers } = useNuxtApp()
const route = useRoute()
const slug = route.params.slug as string

const whatsappHref = computed(() => {
  const number = whatsappListingNumbers[slug] ?? ($whatsappNumbers as Record<LocaleCode, string>)[locale.value as LocaleCode]
  const text = encodeURIComponent(`Hola me gustaría más información sobre ${props.listing.name}`)
  return `https://wa.me/${number}?text=${text}`
})

const createIntersectionObserver = (callback: (entry: IntersectionObserverEntry) => void, threshold = 0.1) => {
  return new IntersectionObserver((entries) => {
    entries.forEach(callback)
  }, { threshold })
}

const observeElement = (observer: IntersectionObserver, selector: string, visibilityRef: { value: boolean } | { [key: string]: boolean }, key?: string) => {
  const element = document.querySelector(selector) as HTMLDivElement
  if (element) {
    if (key) {
      visibilityRef[key] = false
    } else {
      visibilityRef.value = false
    }
    observer.observe(element)
  }
}

const setFixedCtaObserver = () => {
  const typologiesFromObserver = createIntersectionObserver((entry) => {
    observableElementsVisibility.value[entry.target.className] = entry.isIntersecting
  })

  props.observableSelectors.forEach((selector) => {
    observeElement(typologiesFromObserver, selector, observableElementsVisibility.value, selector)
  })

  const samPresentationSectionObserver = createIntersectionObserver((entry) => {
    samPresentationSectionVisible.value = entry.isIntersecting
  })

  observeElement(samPresentationSectionObserver, '#sam-presentation-section', samPresentationSectionVisible)
}

watch(showCta, (newValue) => {
  emit('showCta', newValue)
})

onMounted(() => {
  setTimeout(setFixedCtaObserver)
})
</script>

<script lang="ts">
export default {
  name: 'PdFixedCtasV2'
}
</script>

<template>
  <div
    class="pd-fixed-ctas fixed right-0 flex flex-col z-10 transition-all duration-500 ease-in-out"
    :style="{
      bottom: showCta ? '0' : '-100%'
    }">
    <div class="w-screen px-24 lg:px-60 py-16 md:py-16 flex flex-col md:flex-row justify-center items-start lg:items-center md:justify-between shadow-elevation-light-level-8">
      <div class="flex md:hidden items-center mb-16 lg:mb-8">
        <p class="text-primary-800 text-16 font-medium lg:text-16 !leading-8">
          {{ t('valueProposition') }}
        </p>
      </div>

      <p class="hl-3-m text-pine-600 hidden md:block">
        {{ t('description') }}
      </p>

      <a
        id="pd-bottom-cta-contact-constructor"
        class="roomie-button roomie-button--md roomie-button--primary roomie-button--full-width w-full md:max-w-md"
        :href="whatsappHref"
        target="_blank"
        rel="noopener noreferrer">
        <span class="roomie-button__content">Hablar con asesor</span>
      </a>
    </div>
  </div>
</template>

<style lang="scss">
.lh-roomie-v2 {
  .pd-fixed-ctas {
    @apply border-none;

    background-color: var(--color-gray-600);

    .whatsapp-fixed-cta-label {
      display: none;
    }

    #pd-bottom-cta-contact-constructor {
      @apply px-24 py-12;

      min-height: 4.8rem;

      .roomie-button__content {
        @apply leading-none;
      }

      @media (width >= theme('screens.lg')) {
        @apply cta-18 px-32 py-16;

        min-height: 5.6rem;
      }
    }
  }
}
</style>

<i18n src="./i18n.json" lang="json"></i18n>
